// @flow
import * as React from 'react';

import { RECIPIENT_TYPES } from 'reducers/roleBasedMessagingSearch';
import CenteredLoadingIndicator from 'components/CenteredLoadingIndicator';
import TargetByStudentIdNull from './TargetByStudentIdNull';
import TargetByStudentIdSelectPage from './TargetByStudentIdSelectPage';
import TargetByStudentIdUploadPage from './TargetByStudentIdUploadPage';

import type { TargetByStudentIdPages } from './SelectRecipientPanel';

type Props = {
  onContinue: () => void,
  onPageChange: (page: TargetByStudentIdPages) => void,
  studentIdListRecipientInSelection: ?StudentIdListRecipient,

  // graphql data from container
  loading: boolean,
  schools: ?($Shape<ComposerOrganization>[]),

  // redux dispatchers from container
  selectStudentIdListRecipient: (recipient: StudentIdListRecipient) => void,
  clearSelection: () => *,
};

type State = {
  showNullState: boolean,
  studentIdListUuid: ?string,
  csvName: ?string,
  selectedSchoolUuid: string,
};

class TargetByStudentIdPanel extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      showNullState: false,
      studentIdListUuid:
        props.studentIdListRecipientInSelection && props.studentIdListRecipientInSelection.id,
      csvName:
        props.studentIdListRecipientInSelection && props.studentIdListRecipientInSelection.csvName,
      selectedSchoolUuid: props.studentIdListRecipientInSelection
        ? props.studentIdListRecipientInSelection.orgUuid
        : '',
    };
  }

  componentDidMount() {
    const { schools, studentIdListRecipientInSelection } = this.props;
    if (
      schools &&
      !this.schoolsHaveEnoughStudentIds(schools) &&
      !studentIdListRecipientInSelection
    ) {
      this.showNullState();
    }
  }

  componentDidUpdate(prevProps: Props) {
    if (
      !prevProps.schools &&
      this.props.schools &&
      !this.schoolsHaveEnoughStudentIds(this.props.schools) &&
      !this.props.studentIdListRecipientInSelection
    ) {
      this.showNullState();
    }
  }

  showNullState = () => {
    this.setState({ showNullState: true });
  };

  hideNullState = () => {
    this.setState({ showNullState: false });
  };

  schoolsHaveEnoughStudentIds = (schools: ComposerOrganization[]) => {
    return schools.length ? schools.some(school => school.studentIdPercentage > 0.5) : false;
  };

  addListToSelection = (studentIdList: *, csvName: string) => {
    this.props.selectStudentIdListRecipient({
      type: RECIPIENT_TYPES.STUDENT_ID_LIST_RECIPIENT,
      id: studentIdList.uuid,
      csvName,
      fileUuid: studentIdList.fileUuid,
      includeParents: studentIdList.includeParents,
      includeStudents: studentIdList.includeStudents,
      orgUuid: studentIdList.organizationUuid,
      students: studentIdList.students,
    });
  };

  handleContinue = (studentIdList: *, csvName: string) => {
    this.addListToSelection(studentIdList, csvName);
    this.props.onContinue();
  };

  handleBack = () => {
    this.props.clearSelection();
    this.setState({
      csvName: null,
      studentIdListUuid: null,
    });
  };

  handleOrgSelection = (selectedSchoolUuid: string) => {
    this.setState({ selectedSchoolUuid });
  };

  handleUploadSuccess = (data: { studentIdListUuid: string, csvName: string }) => {
    this.setState({ studentIdListUuid: data.studentIdListUuid, csvName: data.csvName });
  };

  render() {
    if (this.props.loading) {
      return <CenteredLoadingIndicator />;
    }

    const { showNullState, studentIdListUuid, csvName, selectedSchoolUuid } = this.state;

    if (showNullState) {
      return (
        <TargetByStudentIdNull
          data-test="null-page"
          onContinue={this.hideNullState}
          onMount={() => this.props.onPageChange('upload_list_null')}
        />
      );
    }

    return studentIdListUuid && csvName ? (
      <TargetByStudentIdSelectPage
        data-test="select-page"
        onContinue={this.handleContinue}
        onBack={this.handleBack}
        orgUuid={selectedSchoolUuid}
        studentIdListUuid={studentIdListUuid}
        csvName={csvName}
        onMount={() => this.props.onPageChange('list_of_uploaded_users')}
      />
    ) : (
      <TargetByStudentIdUploadPage
        data-test="upload-page"
        schools={this.props.schools}
        onUploadSuccess={this.handleUploadSuccess}
        onOrgSelection={this.handleOrgSelection}
        selectedSchoolUuid={this.state.selectedSchoolUuid}
        onMount={() => this.props.onPageChange('upload_list')}
      />
    );
  }
}

export default TargetByStudentIdPanel;
